import { buildRounds, type Round } from '@/domain/rounds';
import type { Profile, Question } from '@/domain/types';
import { jamesProfile } from './profiles/james';
import { jamesQuestions, jamesRounds } from './questions/james.questions';

/**
 * The content registry.
 *
 * This is the one seam that makes a second creator possible later without
 * touching a single component: everything downstream asks the registry for
 * "the profile at this slug" rather than importing James directly.
 *
 * Adding someone else, one day, is:
 *   1. a new file under content/profiles
 *   2. a new file under content/questions
 *   3. one more entry here
 *
 * Nothing else in the app needs to change. Deliberately no registration flow,
 * no database-backed profiles and no admin — that is all out of scope for the
 * first version, and building the seam is enough.
 */

export interface CreatorContent {
  profile: Profile;
  /** The flat question list. Still the source of truth. */
  questions: Question[];
  /**
   * The same questions, batched. Built once here rather than in a component,
   * so every page reads identical round boundaries and nothing has to memoise
   * a derivation of static content.
   */
  rounds: Round[];
}

const registry: Record<string, CreatorContent> = {
  [jamesProfile.slug]: {
    profile: jamesProfile,
    questions: jamesQuestions,
    rounds: buildRounds(jamesQuestions, jamesRounds),
  },
};

/** The profile the landing page links to while James is the only one. */
export const featuredSlug = jamesProfile.slug;

export function getCreatorContent(slug: string): CreatorContent | undefined {
  return registry[slug.toLowerCase()];
}

export function listCreatorSlugs(): string[] {
  return Object.keys(registry);
}
